import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Center,
  HStack,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { useRedistributeProjectCreditMutation } from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import type { FinishProjectSummary } from "../models";
import { CreditItem } from "./CreditItem";

interface UninvoicedProps {
  summary: FinishProjectSummary;
  closeModal: () => void;
  onCompleted: () => void;
}

const Uninvoiced = ({ summary, closeModal, onCompleted }: UninvoicedProps) => {
  const router = useRouter();
  const {
    id,
    overinvoiced_for_project,
    can_redistribute_credit_automatically,
    underinvoiced_our_work_budget_items,
    underinvoiced_expense_budget_items,
    overinvoiced_our_work_budget_items,
    overinvoiced_expense_budget_items,
  } = summary;

  const [redistributeCredits] = useRedistributeProjectCreditMutation({
    variables: { id: id },
    ...onError(),
    onCompleted,
  });

  const onGoToProject = () => {
    const noNeedToRedirect = router.pathname.match(/projects\/detail/);

    if (noNeedToRedirect) {
      closeModal();
    } else {
      router.push({ pathname: "/projects/detail", query: { id: id } });
    }
  };

  return (
    <Alert
      alignItems="top"
      rowGap="8"
      gridTemplateColumns="max-content 1fr"
      display="grid"
      status="warning"
      variant="top-accent"
    >
      <AlertIcon />
      <div>
        <AlertTitle>
          <Trans>
            Invoiced amount of some budget items do not match its budget
          </Trans>
        </AlertTitle>
        <AlertDescription>
          <div>
            <div className="flex items-center">
              <VStack alignItems={"start"}>
                {!overinvoiced_for_project ? (
                  <p>
                    {can("credit_transfer_withDate") ? (
                      <Trans>
                        You can finish the project. Please mind, this project is
                        not invoiced according to the budget.
                      </Trans>
                    ) : (
                      <Trans>
                        This project is not invoiced according to the budget.
                      </Trans>
                    )}
                  </p>
                ) : (
                  <Trans>
                    Cannot be closed because project is overinvoiced.
                  </Trans>
                )}
                <HStack>
                  <p>
                    <Trans>Budget items with credit problem:</Trans>
                  </p>
                  <Tooltip
                    label={
                      <div
                        className="text-center p-3 text-lg"
                        style={{ maxWidth: 200 }}
                      >
                        <p>
                          <Trans>
                            Invoiced amount and budget must match for every item
                          </Trans>
                        </p>
                      </div>
                    }
                  >
                    <div className="ml-3">
                      <Icon
                        name="info-circle"
                        color="#919EAB"
                        variant="solid"
                      />
                    </div>
                  </Tooltip>
                </HStack>
              </VStack>
            </div>
            <div>
              {underinvoiced_our_work_budget_items.map((i) => (
                <CreditItem key={i.id} item={i} credit="under" />
              ))}
              {underinvoiced_expense_budget_items.map((i) => (
                <CreditItem key={i.id} item={i} credit="under" />
              ))}
              {overinvoiced_our_work_budget_items.map((i) => (
                <CreditItem key={i.id} item={i} credit="over" />
              ))}
              {overinvoiced_expense_budget_items.map((i) => (
                <CreditItem key={i.id} item={i} credit="over" />
              ))}
            </div>
          </div>
        </AlertDescription>
      </div>
      <Center gridColumn="span 2">
        <Button
          _hover={{ bg: "red.200" }}
          colorScheme="grey"
          onClick={() =>
            can_redistribute_credit_automatically
              ? redistributeCredits()
              : onGoToProject()
          }
          variant="outline"
        >
          {can_redistribute_credit_automatically ? (
            <Trans>Redistribute credit automatically</Trans>
          ) : (
            <Trans>Go to project detail</Trans>
          )}
        </Button>
      </Center>
    </Alert>
  );
};

export default observer(Uninvoiced);
